/**
 * SESSION MANAGER - Centralized Session Persistence
 * Fixes: Session loss in Bulk Compare, Cart Optimizer, and tab navigation
 * Updated: Added "Remember Me" functionality
 * Phase 1, Day 1 - Morning Session
 */

const SESSION_KEYS = {
  USER_ID: 'pricepalcompare_user_id',
  USER_EMAIL: 'pricepalcompare_user_email',
  IS_AUTHENTICATED: 'pricepalcompare_is_authenticated',
  USER_DATA: 'pricepalcompare_user_data',
  LAST_ACTIVITY: 'pricepalcompare_last_activity',
  REMEMBER_ME: 'pricepalcompare_remember_me',
};

const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

class SessionManager {
  /**
   * Initialize session from storage
   * Restores session if within timeout period, regardless of Remember Me
   * Remember Me extends timeout from 24h to 30 days
   */
  static init() {
    const rememberMe = localStorage.getItem(SESSION_KEYS.REMEMBER_ME) === 'true';

    // Remember Me: 30 days. Regular session: 24 hours (persists across page refreshes)
    const timeout = rememberMe
      ? 30 * 24 * 60 * 60 * 1000   // 30 days
      : SESSION_TIMEOUT;             // 24 hours

    const lastActivity = localStorage.getItem(SESSION_KEYS.LAST_ACTIVITY);

    if (lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity);
      if (timeSinceActivity > timeout) {
        this.clearSession();
        return null;
      }
    }

    return this.getSession();
  }

  /**
   * Get current session
   */
  static getSession() {
    const userId = localStorage.getItem(SESSION_KEYS.USER_ID);
    const userEmail = localStorage.getItem(SESSION_KEYS.USER_EMAIL);
    const isAuthenticated = localStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED) === 'true';
    const userDataStr = localStorage.getItem(SESSION_KEYS.USER_DATA);
    
    // Validate session completeness
    if (!userId || !userEmail) {
      return null;
    }
    
    let userData = null;
    if (userDataStr) {
      try {
        userData = JSON.parse(userDataStr);
      } catch (e) {
        console.error('[SessionManager] Error parsing user data:', e);
      }
    }
    
    // Update last activity
    this.updateActivity();
    
    return {
      userId,
      userEmail,
      isAuthenticated,
      userData,
    };
  }

  /**
   * Set session data
   */
  static setSession(userId, userEmail, userData = null, rememberMe = false) {
    if (!userId || !userEmail) {
      console.error('[SessionManager] Cannot set session without userId and userEmail');
      return false;
    }
    
    
    localStorage.setItem(SESSION_KEYS.USER_ID, userId);
    localStorage.setItem(SESSION_KEYS.USER_EMAIL, userEmail);
    localStorage.setItem(SESSION_KEYS.IS_AUTHENTICATED, 'true');
    localStorage.setItem(SESSION_KEYS.REMEMBER_ME, rememberMe.toString());
    
    if (userData) {
      localStorage.setItem(SESSION_KEYS.USER_DATA, JSON.stringify(userData));
    }
    
    this.updateActivity();
    
    return true;
  }

  /**
   * Update user data in session
   */
  static updateUserData(userData) {
    if (userData) {
      localStorage.setItem(SESSION_KEYS.USER_DATA, JSON.stringify(userData));
      this.updateActivity();
    }
  }

  /**
   * Clear entire session
   */
  static clearSession() {
    Object.values(SESSION_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Also clear legacy keys
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    
    // Clear sessionStorage for cart/bulk compare
    sessionStorage.clear();
  }

  /**
   * Check if session is valid
   */
  static isValid() {
    const session = this.getSession();
    return session !== null && session.userId && session.userEmail;
  }

  /**
   * Update last activity timestamp
   */
  static updateActivity() {
    localStorage.setItem(SESSION_KEYS.LAST_ACTIVITY, Date.now().toString());
  }

  /**
   * Get user ID from session
   */
  static getUserId() {
    const session = this.getSession();
    return session ? session.userId : null;
  }

  /**
   * Get user email from session
   */
  static getUserEmail() {
    const session = this.getSession();
    return session ? session.userEmail : null;
  }

  /**
   * Debug: Log current session state
   */
  static debug() {
    // Session debug info available via browser DevTools
  }
}

export default SessionManager;
